import { supabase } from '@/integrations/supabase/client';
import type { CritiqueFinding } from '@/lib/slack/editorial-note-tag';

export interface EditorialTagRequest {
  topicId: string;
  finding: Pick<CritiqueFinding, 'dimension' | 'reason' | 'quote' | 'suggestion'>;
  recipient: { kind: 'member' | 'channel'; id: string };
  /** With a channel recipient: the person to mention in it, when a DM was not possible. */
  mentionUserId?: string | null;
  note?: string | null;
}

export interface EditorialTagFailure {
  ok: false;
  code: string;
  message: string;
}

export type EditorialTagResponse =
  { ok: true; via: 'dm' | 'app_dm' | 'channel' } | EditorialTagFailure;

/**
 * Client call for `POST /api/slack/tag-note` (tag a person on an editorial review note).
 * Lives under `src/lib/` because it needs the session token, the same reason
 * `fetch-slack-mentions.ts` does. Never throws: every failure comes back as words the
 * picker can show, and the server's own message is preferred because it says what to do.
 */
export async function postEditorialTag(
  request: EditorialTagRequest,
): Promise<EditorialTagResponse> {
  const { data } = await supabase.auth.getSession();
  const token = data.session?.access_token;
  if (!token) return { ok: false, code: 'unauthorized', message: 'Sign in again to tag people.' };

  try {
    const response = await fetch('/api/slack/tag-note', {
      method: 'POST',
      headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
      body: JSON.stringify({
        topicId: request.topicId,
        finding: {
          dimension: request.finding.dimension,
          reason: request.finding.reason,
          quote: request.finding.quote,
          suggestion: request.finding.suggestion,
        },
        recipient: request.recipient,
        mentionUserId: request.mentionUserId ?? null,
        note: request.note?.trim() || null,
      }),
    });
    const body = (await response.json().catch(() => null)) as {
      ok?: boolean;
      via?: 'dm' | 'app_dm' | 'channel';
      code?: string;
      error?: string;
    } | null;
    if (response.ok && body?.ok && body.via) return { ok: true, via: body.via };
    return {
      ok: false,
      code: body?.code ?? `http_${response.status}`,
      message: body?.error ?? 'Could not send that tag. Nothing was sent.',
    };
  } catch {
    return { ok: false, code: 'network', message: 'Could not reach Mora. Nothing was sent.' };
  }
}
